// Copyright 2025, University of Maryland, All Rights Reserved
"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { $getRoot, type EditorState, type LexicalEditor } from "lexical"

import { CharacterCounter } from "./character-counter"
import { Editor } from "./editor"
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS } from "./plugins"

const HARD_LIMIT = 20480 // 20KB

export function MarkdownEditor({
  value,
  onChange,
  onKeyDown,
  showCharacterCount = true,
  ariaLabel,
  toolbar,
}: {
  value?: string
  onChange?: (markdown: string) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  showCharacterCount?: boolean
  ariaLabel?: string
  toolbar?: "full" | "minimal"
}) {
  const [charCount, setCharCount] = useState(value?.length || 0)
  const editorRef = useRef<LexicalEditor | null>(null)
  const isInternalChange = useRef(false)
  const lastSyncedValue = useRef<string | undefined>(undefined)

  // Callback when editor is ready - captures the editor reference immediately
  const handleEditorReady = useCallback((editor: LexicalEditor) => {
    editorRef.current = editor
  }, [])

  // Sync external value changes to the editor
  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return

    // Skip if this is an internal change (from user typing)
    if (isInternalChange.current) {
      isInternalChange.current = false
      return
    }

    // Skip if value hasn't changed from what we last synced
    if (value === lastSyncedValue.current) return

    // Update editor content from external value
    editor.update(() => {
      const root = $getRoot()
      root.clear()
      if (value) {
        $convertFromMarkdownString(value, TRANSFORMERS)
      }
    })

    lastSyncedValue.current = value
    setCharCount(value?.length || 0)
  }, [value])

  // Initial editor state - only used on first mount
  const initialEditorState = useCallback(() => {
    if (value) {
      $convertFromMarkdownString(value, TRANSFORMERS)
      lastSyncedValue.current = value
    }
  }, [value])

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const markdown = $convertToMarkdownString(TRANSFORMERS)
        setCharCount(markdown.length)

        // Mark that this change came from user input so we don't re-sync
        isInternalChange.current = true
        lastSyncedValue.current = markdown

        // Enforce hard limit
        if (markdown.length <= HARD_LIMIT) {
          onChange?.(markdown)
        }
      })
    },
    [onChange]
  )

  return (
    <div className="space-y-2">
      <div onKeyDown={onKeyDown}>
        <Editor
          editorState={initialEditorState}
          onChange={handleChange}
          onEditorReady={handleEditorReady}
          ariaLabel={ariaLabel}
          toolbar={toolbar}
        />
      </div>
      {showCharacterCount && (
        <div className="px-2">
          <CharacterCounter count={charCount} />
        </div>
      )}
    </div>
  )
}
